import React, { useState } from 'react';
import { Clock, ChevronRight, AlertCircle, CheckCircle2, Package, Truck, Globe, Smartphone, User, XCircle, Search, Hash, Phone, Calendar } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export type OrderStatus = 'Pending' | 'Accepted' | 'Picking' | 'Searching for Driver' | 'Driver Assigned' | 'Out for Delivery' | 'Delivered' | 'Delivery Attempt Failed' | 'Held' | 'Cancelled' | 'Ready for Pickup' | 'Return Requested' | 'Waiting Return Confirmation' | 'Refunded';
export type Channel = 'Website' | 'Aggregator';
export type PaymentType = 'COD' | 'Visa' | 'Mada' | 'ApplePay' | 'Tamara' | 'Tabby' | 'Prepaid';
export interface DriverInfo {
  name: string;
  phone: string;
  vehiclePlate: string;
  rating: number;
  avatar?: string;
}
export interface HistoryEvent {
  id: string;
  timestamp: string;
  status: OrderStatus;
  message: string;
  actor: 'Pharmacist' | 'System' | 'Driver' | 'Customer Service';
  actorName: string;
}
export interface BranchRouting {
  branchName: string;
  timestamp: string;
  reason?: string;
}
export interface Order {
  id: string;
  customerName: string;
  itemsCount: number;
  totalAmount: number;
  status: OrderStatus;
  channel: Channel;
  paymentType: PaymentType;
  createdAt: string;
  slaMinutes: number;
  priority: 'High' | 'Medium' | 'Low';
  rejectionReason?: string;
  holdReason?: string;
  driverInfo?: DriverInfo;
  courierName?: string;
  courierPhone?: string;
  discountAmount?: number;
  couponCode?: string;
  customerAddress?: string;
  customerPhone?: string;
  // New Detailed Fields
  customerCity?: string;
  customerDistrict?: string;
  customerAge?: number;
  customerGender?: 'Male' | 'Female';
  isLoyaltyCustomer?: boolean;
  loyaltyPointsUsed?: number;
  qitafAmount?: number;
  walletAmount?: number;
  // ---
  isPickup?: boolean;
  otp?: string;
  otpVerified?: boolean;
  sourceOrderId?: string;
  previousStatus?: OrderStatus;
  requiresPrescription?: boolean;
  prescriptionVerified?: boolean;
  prescriptionImage?: string;
  routingHistory?: BranchRouting[];
  currentBranch?: string;
  history: HistoryEvent[];
}
interface OrderQueueProps {
  orders: Order[];
  onSelectOrder: (order: Order) => void;
}
export function OrderQueue({
  orders,
  onSelectOrder
}: OrderQueueProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneQuery, setPhoneQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState('');
  const filteredOrders = orders.filter(order => {
    // Exclude terminal statuses from the active queue
    const isTerminalStatus = ['Delivered', 'Cancelled', 'Rejected'].includes(order.status);
    if (isTerminalStatus) return false;
    const matchesSearch = order.id.toLowerCase().includes(searchQuery.toLowerCase()) || order.customerName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPhone = order.customerPhone?.includes(phoneQuery) || !phoneQuery;
    const matchesSource = sourceFilter === 'all' || order.channel === sourceFilter;
    const matchesDate = !dateFilter || order.createdAt.includes(dateFilter);
    return matchesSearch && matchesPhone && matchesSource && matchesDate;
  });
  return <div className="space-y-6" data-id="element-550">
      <div className="flex items-center justify-between" data-id="element-551">
        <div data-id="element-552">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-553">Order Queue</h2>
          <p className="text-slate-500" data-id="element-554">
            Manage incoming orders and fulfillment SLAs
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-4 gap-4" data-id="element-555">
        <div className="space-y-2" data-id="element-556">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-557">
            <Hash className="w-3 h-3" data-id="element-558" />
            Order ID / Name
          </label>
          <div className="relative" data-id="element-559">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-560" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search ID or Name..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-561" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-562">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-563">
            <Phone className="w-3 h-3" data-id="element-564" />
            Customer Phone
          </label>
          <div className="relative" data-id="element-565">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-566" />
            <input type="text" value={phoneQuery} onChange={e => setPhoneQuery(e.target.value)} placeholder="050..." className="w-full pl-10 pr-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-567" />
          </div>
        </div>

        <div className="space-y-2" data-id="element-568">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-569">
            <Globe className="w-3 h-3" data-id="element-570" />
            Source
          </label>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2 appearance-none" data-id="element-571">
            <option value="all" data-id="element-572">All Sources</option>
            <option value="Website" data-id="element-573">Website</option>
            <option value="Aggregator" data-id="element-574">Aggregator</option>
          </select>
        </div>

        <div className="space-y-2" data-id="element-575">
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2" data-id="element-576">
            <Calendar className="w-3 h-3" data-id="element-577" />
            Date
          </label>
          <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border-transparent focus:bg-white focus:border-blue-500 rounded-xl text-sm transition-all outline-none border-2" data-id="element-578" />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-579">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-580">
          <div data-id="element-581">Order ID</div>
          <div data-id="element-582">Customer</div>
          <div data-id="element-583">Source</div>
          <div data-id="element-584">Status</div>
          <div data-id="element-585">Shipping / Driver</div>
          <div data-id="element-586">SLA</div>
          <div data-id="element-587">Amount</div>
          <div className="w-10" data-id="element-588"></div>
        </div>

        <div className="divide-y divide-slate-100" data-id="element-589">
          {filteredOrders.map(order => <button key={order.id} onClick={() => onSelectOrder(order)} className={cn('w-full grid grid-cols-[1fr_1.5fr_1fr_1fr_1.5fr_1fr_1fr_auto] gap-4 px-6 py-5 items-center transition-colors text-left group', order.slaMinutes < 10 ? 'bg-rose-50/50 hover:bg-rose-50' : 'hover:bg-slate-50')} data-id="element-590">
              <div className="font-mono font-bold text-blue-600" data-id="element-591">
                #{order.id}
              </div>

              <div data-id="element-592">
                <p className="font-semibold text-slate-900" data-id="element-593">
                  {order.customerName}
                </p>
                <p className="text-xs text-slate-500" data-id="element-594">
                  {order.itemsCount} items
                </p>
              </div>

              <div className="flex items-center gap-2" data-id="element-595">
                {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-596">
                    <Globe className="w-3 h-3" data-id="element-597" />
                    Website
                  </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-598">
                    <Smartphone className="w-3 h-3" data-id="element-599" />
                    Aggregator
                  </div>}
              </div>

              <div data-id="element-600">
                <StatusBadge status={order.status} data-id="element-601" />
              </div>

              <div data-id="element-602">
                {order.isPickup ? <div className="inline-flex items-center gap-1.5 rounded-md bg-purple-50 px-2 py-1 text-[10px] font-bold uppercase text-purple-700" data-id="element-603">
                    <User className="h-3 w-3" data-id="element-604" />
                    Self Pickup
                  </div> : order.courierName || order.driverInfo ? <div className="space-y-1" data-id="element-605">
                    {order.courierName && <div className="flex flex-col gap-0.5" data-id="element-606">
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 uppercase" data-id="element-607">
                          <Globe className="w-3 h-3 text-blue-500" data-id="element-608" />
                          {order.courierName}
                        </div>
                        {order.courierPhone && <div className="flex items-center gap-1.5 text-[9px] font-medium text-slate-400 pl-4" data-id="element-609">
                            <Phone className="w-2.5 h-2.5" data-id="element-610" />
                            {order.courierPhone}
                          </div>}
                      </div>}
                    {order.driverInfo && <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-600 uppercase" data-id="element-611">
                        <User className="w-3 h-3 text-emerald-500" data-id="element-612" />
                        {order.driverInfo.name}
                      </div>}
                  </div> : <span className="text-[10px] font-bold text-slate-300 uppercase italic" data-id="element-613">
                    Not Assigned
                  </span>}
              </div>

              <div className="flex items-center gap-2" data-id="element-614">
                <div className={cn('flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase', order.slaMinutes < 10 ? 'bg-red-50 text-red-700' : order.slaMinutes < 20 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700')} data-id="element-615">
                  <Clock className="w-3 h-3" data-id="element-616" />
                  {order.slaMinutes}m
                </div>
              </div>

              <div data-id="element-617">
                <p className="font-bold text-slate-900" data-id="element-618">
                  {order.totalAmount.toFixed(2)} SAR
                </p>
                <p className={cn('text-[10px] font-bold uppercase', order.paymentType === 'COD' ? 'text-amber-600' : 'text-emerald-600')} data-id="element-619">
                  {order.paymentType}
                </p>
              </div>

              <div className="flex items-center gap-3" data-id="element-620">
                {order.requiresPrescription && <div className={cn('w-2 h-2 rounded-full', order.prescriptionVerified ? 'bg-emerald-500' : 'bg-purple-500 animate-pulse')} title={order.prescriptionVerified ? 'Prescription Verified' : 'Prescription Verification Required'} data-id="element-621" />}
                <div className="text-slate-300 group-hover:text-blue-600 transition-colors" data-id="element-622">
                  <ChevronRight className="w-5 h-5" data-id="element-623" />
                </div>
              </div>
            </button>)}
        </div>
      </div>
    </div>;
}
function StatusBadge({
  status
}: {
  status: OrderStatus;
}) {
  const styles: Record<OrderStatus, string> = {
    Pending: 'bg-blue-50 text-blue-700 border-blue-100',
    Accepted: 'bg-indigo-50 text-indigo-700 border-indigo-100',
    Picking: 'bg-amber-50 text-amber-700 border-amber-100',
    'Searching for Driver': 'bg-sky-50 text-sky-700 border-sky-100 animate-pulse',
    'Driver Assigned': 'bg-violet-50 text-violet-700 border-violet-100',
    'Out for Delivery': 'bg-orange-50 text-orange-700 border-orange-100',
    Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    'Delivery Attempt Failed': 'bg-rose-50 text-rose-700 border-rose-100',
    Held: 'bg-slate-100 text-slate-700 border-slate-200',
    Cancelled: 'bg-red-50 text-red-700 border-red-100',
    'Ready for Pickup': 'bg-purple-50 text-purple-700 border-purple-100',
    'Return Requested': 'bg-rose-50 text-rose-700 border-rose-100',
    'Waiting Return Confirmation': 'bg-amber-50 text-amber-700 border-amber-100',
    Refunded: 'bg-slate-50 text-slate-700 border-slate-100'
  };
  const icons: Record<OrderStatus, any> = {
    Pending: AlertCircle,
    Accepted: CheckCircle2,
    Picking: Package,
    'Searching for Driver': Truck,
    'Driver Assigned': User,
    'Out for Delivery': Truck,
    Delivered: CheckCircle2,
    'Delivery Attempt Failed': AlertCircle,
    Held: Clock,
    Cancelled: XCircle,
    'Ready for Pickup': Package,
    'Return Requested': AlertCircle,
    'Waiting Return Confirmation': Clock,
    Refunded: CheckCircle2
  };
  const Icon = icons[status] || AlertCircle;
  return <div className={cn('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border', styles[status])} data-id="element-624">
      <Icon className="w-3 h-3" data-id="element-625" />
      {status}
    </div>;
}